import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface LandingHeaderProps {
  onLoginClick: () => void;
}

export function LandingHeader({ onLoginClick }: LandingHeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Determine if we've scrolled past the hero section (roughly 100vh)
      const heroHeight = window.innerHeight;
      const hasScrolledPastHero = currentScrollY > heroHeight * 0.8;
      
      setIsScrolled(hasScrolledPastHero);

      // Determine which section is currently in view
      const sections = ["home", "how-it-works", "why-airtalent", "faq", "contact"];
      const sectionElements = sections.map(id => document.getElementById(id)).filter(Boolean);
      
      let currentSection = "home";
      
      for (const section of sectionElements) {
        if (section) {
          const rect = section.getBoundingClientRect();
          // If section is in the upper half of the viewport, consider it active
          if (rect.top <= window.innerHeight / 2 && rect.bottom >= 0) {
            currentSection = section.id;
          }
        }
      }
      
      setActiveSection(currentSection);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <div 
        className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-300 ease-in-out ${
          isScrolled 
            ? 'bg-[#004aad]/95 backdrop-blur-md shadow-lg py-2' 
            : 'bg-transparent py-4'
        }`}
      >
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center min-h-[48px] sm:min-h-[56px] lg:h-16 w-full">
          {/* Desktop Layout */}
          <div className="hidden lg:flex w-full items-center justify-between max-w-full overflow-hidden">
            {/* Left spacer */}
            <div className="flex-1"></div>
            
            {/* Centered Navigation */}
            <nav className="flex items-center gap-1 xl:gap-2 bg-white/10 rounded-xl p-1 backdrop-blur-sm flex-shrink-0 max-w-fit">
              <div className={`rounded-lg px-2 lg:px-3 xl:px-4 py-2 ${
                activeSection === "home" ? 'bg-gray-800' : ''
              }`}>
                <a href="#home" className={`text-xs lg:text-sm font-bold space-grotesk transition-colors ${
                  activeSection === "home" ? 'text-white' : 'text-white/80 hover:text-white'
                }`}>
                  HOME
                </a>
              </div>
              <div className={`rounded-lg px-2 lg:px-3 xl:px-4 py-2 ${
                activeSection === "how-it-works" ? 'bg-gray-800' : ''
              }`}>
                <a href="#how-it-works" className={`text-xs lg:text-sm font-medium space-grotesk transition-colors ${
                  activeSection === "how-it-works" ? 'text-white font-bold' : 'text-white/80 hover:text-white'
                }`}>
                  HOW IT WORKS
                </a>
              </div>
              <div className={`rounded-lg px-2 lg:px-3 xl:px-4 py-2 ${
                activeSection === "why-airtalent" ? 'bg-gray-800' : ''
              }`}>
                <a href="#why-airtalent" className={`text-xs lg:text-sm font-medium space-grotesk transition-colors ${
                  activeSection === "why-airtalent" ? 'text-white font-bold' : 'text-white/80 hover:text-white'
                }`}>
                  WHY AIRTALENT
                </a>
              </div>
              <div className={`rounded-lg px-2 lg:px-3 xl:px-4 py-2 ${
                activeSection === "faq" ? 'bg-gray-800' : ''
              }`}>
                <a href="#faq" className={`text-xs lg:text-sm font-medium space-grotesk transition-colors ${
                  activeSection === "faq" ? 'text-white font-bold' : 'text-white/80 hover:text-white'
                }`}>
                  FAQ
                </a>
              </div>
              <div className={`rounded-lg px-2 lg:px-3 xl:px-4 py-2 ${
                activeSection === "contact" ? 'bg-gray-800' : ''
              }`}>
                <a href="#contact" className={`text-xs lg:text-sm font-medium space-grotesk transition-colors ${
                  activeSection === "contact" ? 'text-white font-bold' : 'text-white/80 hover:text-white'
                }`}>
                  CONTACT
                </a>
              </div>
              <div className="rounded-lg px-2 lg:px-3 xl:px-4 py-2 bg-gradient-to-b from-orange-400 to-orange-600">
                <a href="https://calendly.com/lisset-airtalent/30min" target="_blank" rel="noopener noreferrer" className="text-xs lg:text-sm font-bold text-white space-grotesk transition-all hover:opacity-90">
                  BOOK DEMO
                </a>
              </div>
            </nav>
            
            {/* Right side with login button aligned to end */}
            <div className="flex-1 flex justify-end items-center">
              <button
                onClick={onLoginClick}
                className="bg-white/10 hover:bg-white/20 text-white font-medium px-4 lg:px-6 py-2.5 lg:py-3 rounded-lg text-xs lg:text-sm space-grotesk transition-all backdrop-blur-sm flex-shrink-0 mr-14"
              >
                LOGIN
              </button>
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="flex lg:hidden w-full justify-between items-center gap-2 sm:gap-4">
            {/* Hamburger Menu Button */}
            <Button
              variant="outline"
              size="icon"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="bg-white/10 border-white/20 hover:bg-white/20 text-white flex-shrink-0 h-9 w-9 sm:h-10 sm:w-10"
            >
              {isMobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </Button>
            
            {/* Right-aligned login button */}
            <button
              onClick={onLoginClick}
              className="bg-white/10 hover:bg-white/20 text-white font-medium px-3 sm:px-6 py-2 rounded-lg text-xs sm:text-sm space-grotesk transition-all backdrop-blur-sm flex-shrink-0 whitespace-nowrap mr-2"
            >
              LOGIN
            </button>
          </div>

          {/* Mobile Sliding Menu */}
          <div className={`
            lg:hidden fixed top-0 left-0 h-screen w-64 bg-gradient-to-tr from-[#594FE0] from-[1.7%] via-[#4175E6] via-[45.57%] to-[#34C7FA] to-[96.2%] z-[55]
            transform transition-transform duration-300 ease-in-out
            ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          `}>
            <div className="flex flex-col h-full">
              {/* Header with close button and logo */}
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-white hover:bg-white/10"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Navigation */}
              <nav className="flex-1 p-6 space-y-3">
                <a 
                  href="#home" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-5 py-4 rounded-xl text-white font-semibold space-grotesk transition-all text-base border-b border-white/10 ${
                    activeSection === "home" 
                      ? 'bg-white/20 shadow-lg' 
                      : 'hover:bg-white/10 hover:translate-x-1'
                  }`}
                >
                  HOME
                </a>
                <a 
                  href="#how-it-works" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-5 py-4 rounded-xl text-white font-semibold space-grotesk transition-all text-base border-b border-white/10 ${
                    activeSection === "how-it-works" 
                      ? 'bg-white/20 shadow-lg' 
                      : 'hover:bg-white/10 hover:translate-x-1'
                  }`}
                >
                  HOW IT WORKS
                </a>
                <a 
                  href="#why-airtalent" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-5 py-4 rounded-xl text-white font-semibold space-grotesk transition-all text-base border-b border-white/10 ${
                    activeSection === "why-airtalent" 
                      ? 'bg-white/20 shadow-lg' 
                      : 'hover:bg-white/10 hover:translate-x-1'
                  }`}
                >
                  WHY AIRTALENT
                </a>
                <a 
                  href="#faq" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-5 py-4 rounded-xl text-white font-semibold space-grotesk transition-all text-base border-b border-white/10 ${
                    activeSection === "faq" 
                      ? 'bg-white/20 shadow-lg' 
                      : 'hover:bg-white/10 hover:translate-x-1'
                  }`}
                >
                  FAQ
                </a>
                <a 
                  href="#contact" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-5 py-4 rounded-xl text-white font-semibold space-grotesk transition-all text-base border-b border-white/10 ${
                    activeSection === "contact" 
                      ? 'bg-white/20 shadow-lg' 
                      : 'hover:bg-white/10 hover:translate-x-1'
                  }`}
                >
                  CONTACT
                </a>
                
                {/* Separator */}
                <div className="py-2"></div>
                
                <a 
                  href="https://calendly.com/lisset-airtalent/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-5 py-4 rounded-xl bg-gradient-to-b from-orange-400 to-orange-600 text-white font-bold space-grotesk transition-all hover:opacity-90 hover:scale-105 shadow-lg text-base text-center"
                >
                  BOOK DEMO
                </a>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
